/*
 * Look builder — sections/look-builder.liquid (home FW26, 2026-09-25).
 *
 * Curated looks (section blocks, 2026-10-08): the section renders the pieces of
 * the look of the period itself (no recommendations fetch, no model picking);
 * this element only counts them, paints the card squares and runs the Add
 * buttons. In the theme editor every Look block is rendered hidden and
 * `shopify:block:select` shows the selected one.
 *
 * 0. Model photo (automatic mode only, `data-look-model-auto`): measures tiny
 *    thumbnails of the product's visible images #2–#6 on a canvas and swaps in
 *    the first full-length on-model shot — head near the top, feet near the
 *    bottom, narrow silhouette through the middle and at the feet. Packshots
 *    (garment ends well above the bottom edge / wide middle) and close-ups
 *    (cut off at the bottom, wide) never pass. Tuned on UK/US/DE product media
 *    2026-09-25. No match (or a canvas error) keeps the server-rendered photo.
 *    A confirmed full-length shot marks the figure `is-full-length`: only then
 *    may the CSS stretch the box taller than the photo (cropping empty studio
 *    at the sides). Otherwise the box keeps the photo's 3:4, so a close-up is
 *    never cut through the face. With a fixed photo (#look tag, photo number,
 *    override) the one photo is measured the same way (data-look-model-thumb).
 * 1. Fills the reserved card slots with the product's complementary
 *    recommendations (the section rendered through the product-recommendations
 *    endpoint, so the cards are the same server-rendered Liquid), topped up with
 *    related products — other product types first — when fewer come back.
 *    A candidate whose title is already on show (two colours of one product)
 *    is skipped. Unfilled slots are removed; with fewer than two pieces the
 *    section switches to a centred model + card layout (look-builder--few).
 *    Runs when the section nears the viewport.
 *    Card photos whose backdrop is not the studio grey get their square painted
 *    in the photo's own edge colour (no crop, no visible bars).
 * 2. "Add" opens the theme's quick-add drawer (quick-cart-drawer.js) for the
 *    card's product, so a size / colour is always chosen before anything is
 *    added. Without the drawer it falls back to the product page.
 * 3. Focus: when the drawer closes, focus returns to the Add button that opened
 *    it; when the drawer closed because the item was added (the cart drawer is
 *    open), the cart drawer is told to return focus there when it closes.
 *    While the drawer is open for one of these buttons, Tab / Shift+Tab wrap
 *    inside it and Escape closes it from anywhere (the theme drawer's own trap
 *    counts a hidden input as its last stop, so focus used to escape under the
 *    backdrop), and the drawer panel is exposed as a named modal dialog. The
 *    shared quick-cart-drawer.js is not changed.
 */
if (!customElements.get('look-builder')) {
  const LOOK_BUILDER_TABBABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

  class LookBuilder extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick || this.handleClick.bind(this);
      this.onDrawerClosed = this.onDrawerClosed || this.handleDrawerClosed.bind(this);
      this.addEventListener('click', this.onClick);
      document.addEventListener('closed', this.onDrawerClosed);
      this.watchFootLayout();
      if (window.Shopify?.designMode) {
        this.onBlockSelect = this.onBlockSelect || this.handleBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      const needsPieces = !!(this.dataset.recsUrl && this.querySelector('[data-look-slot]'));
      const needsModel = !!this.querySelector('[data-look-model-auto], [data-look-model-thumb]');

      const start = () => {
        if (needsModel) this.pickModel();
        if (needsPieces) {
          this.loadPieces();
        } else {
          this.syncPieceCount();
          this.paintCardBackdrops();
        }
      };
      if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
        start();
        return;
      }
      // Well ahead of the browser's own lazy-load distance, so the model
      // photo is normally swapped before its first image is even requested.
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.observer.disconnect();
            start();
          }
        },
        { rootMargin: '1600px 0px' }
      );
      this.observer.observe(this);
    }

    async pickModel() {
      const figure = this.querySelector('[data-look-model]');
      if (!figure || this.modelPicked) return;
      this.modelPicked = true;

      if (!figure.hasAttribute('data-look-model-auto')) {
        const metrics = await this.measure(figure.dataset.lookModelThumb);
        if (metrics && LookBuilder.isFullLength(metrics)) figure.classList.add('is-full-length');
        return;
      }

      for (const candidate of figure.querySelectorAll('template[data-look-model-candidate]')) {
        const metrics = await this.measure(candidate.dataset.thumb);
        if (!metrics || !LookBuilder.isFullLength(metrics)) continue;
        if (!candidate.hasAttribute('data-current')) {
          const next = candidate.content.querySelector('img');
          const current = figure.querySelector('img');
          if (!next || !current) continue;
          current.replaceWith(document.importNode(next, true));
        }
        figure.classList.add('is-full-length');
        return;
      }
    }

    static isFullLength(m) {
      return m.bottom >= 0.9 && m.top <= 0.2 && m.head <= 0.3 && m.middle <= 0.42 && m.low <= 0.4;
    }

    measure(url, reader = LookBuilder.silhouette) {
      return new Promise((resolve) => {
        if (!url) {
          resolve(null);
          return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.decoding = 'async';
        img.onerror = () => resolve(null);
        img.onload = () => {
          try {
            resolve(reader(img));
          } catch (error) {
            resolve(null);
          }
        };
        img.src = url;
      });
    }

    // The photo's backdrop colour: median of the two outer columns on each side
    // of a 24px-wide copy. null when the edge is transparent (cut-out PNG), busy
    // (less than half of it near the median: a scene, not a backdrop), or
    // already close to the studio grey. A model's arm touching the edge is fine.
    static edgeColour(img) {
      const w = 24;
      const h = Math.max(8, Math.round((w * img.naturalHeight) / img.naturalWidth));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      const data = ctx.getImageData(0, 0, w, h).data;
      const pixels = [];
      for (let y = 0; y < h; y++) {
        for (const x of [0, 1, w - 2, w - 1]) {
          const i = (y * w + x) * 4;
          if (data[i + 3] < 250) return null;
          pixels.push([data[i], data[i + 1], data[i + 2]]);
        }
      }
      pixels.sort((a, b) => a[0] + a[1] + a[2] - (b[0] + b[1] + b[2]));
      const median = pixels[Math.floor(pixels.length / 2)];
      const near = (p, q, limit) => Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]) <= limit;
      const uniform = pixels.filter((p) => near(p, median, 36)).length / pixels.length;
      if (uniform < 0.5 || near(median, [239, 239, 237], 10)) return null;
      return `rgb(${median[0]}, ${median[1]}, ${median[2]})`;
    }

    paintCardBackdrops() {
      this.querySelectorAll('[data-look-piece] [data-look-thumb]:not([data-look-painted])').forEach(async (media) => {
        media.setAttribute('data-look-painted', '');
        const colour = await this.measure(media.dataset.lookThumb, LookBuilder.edgeColour);
        if (colour) media.style.backgroundColor = colour;
      });
    }

    // Pieces of the look on show (curated looks: the one Look block that is not
    // hidden; product of the week: the only look). The count drives the CSS
    // placements through data-pieces on the look.
    syncPieceCount() {
      const look = this.querySelector('.look-builder__look:not([hidden])') || this;
      const count = look.querySelectorAll('[data-look-piece]').length;
      look.dataset.pieces = String(count);
      this.dataset.pieces = String(count);
      this.classList.toggle('look-builder--few', count < 2);
    }

    // Theme editor: show the Look block the merchant selected.
    handleBlockSelect(event) {
      const look = event.target && event.target.closest ? event.target.closest('.look-builder__look') : null;
      if (!look || !this.contains(look)) return;
      this.querySelectorAll('.look-builder__look').forEach((el) => {
        el.hidden = el !== look;
      });
      this.syncPieceCount();
      this.syncFootLayout();
    }

    // Row-by-row share of pixels that differ from the studio background
    // (median of the corner pixels), on a 48px-wide copy of the image.
    static silhouette(img) {
      const w = 48;
      const h = Math.max(8, Math.round((w * img.naturalHeight) / img.naturalWidth));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      const data = ctx.getImageData(0, 0, w, h).data;
      const pixel = (x, y) => {
        const i = (y * w + x) * 4;
        return [data[i], data[i + 1], data[i + 2]];
      };
      const corners = [
        [0, 0], [1, 0], [0, 1], [w - 1, 0], [w - 2, 0], [w - 1, 1], [0, h - 1], [w - 1, h - 1]
      ]
        .map(([x, y]) => pixel(x, y))
        .sort((a, b) => a[0] + a[1] + a[2] - (b[0] + b[1] + b[2]));
      const bg = corners[4];
      const coverage = [];
      for (let y = 0; y < h; y++) {
        let count = 0;
        for (let x = 0; x < w; x++) {
          const [r, g, b] = pixel(x, y);
          if (Math.hypot(r - bg[0], g - bg[1], b - bg[2]) > 32) count++;
        }
        coverage.push(count / w);
      }
      const rows = coverage.map((c, i) => (c > 0.04 ? i : -1)).filter((i) => i >= 0);
      if (!rows.length) return null;
      const mean = (from, to) => {
        const a = Math.floor(from * h);
        const b = Math.max(a + 1, Math.floor(to * h));
        let sum = 0;
        for (let i = a; i < b && i < h; i++) sum += coverage[i];
        return sum / Math.max(1, b - a);
      };
      const top = rows[0] / h;
      return {
        top,
        bottom: (rows[rows.length - 1] + 1) / h,
        head: mean(top, top + 0.06),
        middle: mean(0.45, 0.75),
        low: mean(0.88, 0.97)
      };
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('closed', this.onDrawerClosed);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.releaseDrawer();
    }

    // Price + Add share a row when they fit. Long labels (DE "Hinzufügen")
    // wrap on some cards only, so once ANY card's row wraps, every card in
    // the section stacks the button under the price — one consistent layout.
    watchFootLayout() {
      if (this.resizeObserver || !('ResizeObserver' in window)) return;
      let frame = null;
      this.resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.syncFootLayout());
      });
      this.resizeObserver.observe(this);
      if (document.fonts?.ready) document.fonts.ready.then(() => this.syncFootLayout());
    }

    syncFootLayout() {
      this.classList.remove('look-builder--stacked-foot');
      const wraps = [...this.querySelectorAll('[data-look-piece] .look-builder__card-foot')].some((foot) => {
        const price = foot.querySelector('.look-builder__card-price');
        const button = foot.querySelector('.look-builder__add');
        return price && button && button.offsetTop > price.offsetTop + 4;
      });
      this.classList.toggle('look-builder--stacked-foot', wraps);
    }

    async fetchCandidates(url, retry = true) {
      if (!url) return [];
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return [...doc.querySelectorAll('[data-look-builder-recs] [data-look-piece]')];
      } catch (error) {
        if (!retry) return [];
        // One retry after a pause: a transient error should not leave the look empty.
        await new Promise((resolve) => setTimeout(resolve, 1200));
        return this.fetchCandidates(url, false);
      }
    }

    async loadPieces() {
      if (this.loading) return;
      this.loading = true;

      const slots = [...this.querySelectorAll('[data-look-slot]')];
      const present = [...this.querySelectorAll('[data-look-piece]')];
      const usedIds = new Set(present.map((piece) => piece.dataset.productId));
      const usedTypes = new Set(present.map((piece) => piece.dataset.productType).filter(Boolean));
      // Two colours of one product carry the same title, and a "Natural color"
      // edition repeats it inside a longer one: show that product once.
      const usedTitles = new Set(present.map((piece) => piece.dataset.productTitle).filter(Boolean));
      const picked = [];

      const sameTitle = (title) =>
        !!title && [...usedTitles].some((used) => used === title || (Math.min(used.length, title.length) > 12 && (used.includes(title) || title.includes(used))));
      const isNew = (piece) => !usedIds.has(piece.dataset.productId) && !sameTitle(piece.dataset.productTitle);
      const take = (piece) => {
        usedIds.add(piece.dataset.productId);
        if (piece.dataset.productType) usedTypes.add(piece.dataset.productType);
        if (piece.dataset.productTitle) usedTitles.add(piece.dataset.productTitle);
        picked.push(piece);
      };

      const complementary = await this.fetchCandidates(this.dataset.recsUrl);
      for (const piece of complementary) {
        if (picked.length >= slots.length) break;
        if (isNew(piece)) take(piece);
      }

      if (picked.length < slots.length && this.dataset.relatedUrl) {
        const related = (await this.fetchCandidates(this.dataset.relatedUrl)).filter(isNew);
        // A look wants different kinds of pieces: other product types first.
        for (const piece of related) {
          if (picked.length >= slots.length) break;
          const type = piece.dataset.productType;
          if (isNew(piece) && type && !usedTypes.has(type)) take(piece);
        }
        for (const piece of related) {
          if (picked.length >= slots.length) break;
          if (isNew(piece)) take(piece);
        }
      }

      slots.forEach((slot, index) => {
        const piece = picked[index];
        if (piece) {
          slot.replaceWith(document.importNode(piece, true));
        } else {
          slot.remove();
        }
      });

      this.querySelectorAll('[data-look-piece] [data-look-piece-index]').forEach((indexEl, index) => {
        indexEl.textContent = String(index + 1);
      });
      this.syncPieceCount();
      this.classList.add('is-loaded');
      this.syncFootLayout();
      this.paintCardBackdrops();
    }

    handleClick(event) {
      const button = event.target.closest('[data-look-add]');
      if (!button || button.disabled || !this.contains(button)) return;

      const drawer = document.querySelector('quick-cart-drawer');
      if (!drawer || typeof drawer.fetchProductForQuickCartDrawer !== 'function') {
        if (button.dataset.productHref) window.location.href = button.dataset.productHref;
        return;
      }

      event.preventDefault();
      if (button.classList.contains('is--loading')) return;

      this.lastTrigger = button;
      button.setAttribute('aria-expanded', 'true');
      this.holdDrawer(drawer);
      // The drawer reads target.closest('[data-product-url]') and, with the
      // --recommendations modifier, skips its product-card option syncing.
      drawer.fetchProductForQuickCartDrawer({ target: button });
    }

    // Modal behaviour for the theme's quick-add drawer while one of these
    // buttons opened it (released on the drawer's `closed` event).
    holdDrawer(drawer) {
      this.releaseDrawer();
      const panel = drawer.querySelector('.quick-cart-drawer__blocks') || drawer;
      const title = drawer.querySelector('.quick-cart-drawer__header .h5, .quick-cart-drawer__header p');
      if (title && !title.id) title.id = 'QuickCartDrawerTitle';
      const saved = ['role', 'aria-modal', 'aria-labelledby'].map((name) => [name, panel.getAttribute(name)]);
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      if (title) panel.setAttribute('aria-labelledby', title.id);

      let backwards = false;
      const isOpen = () => drawer.classList.contains('is--open');
      const stops = () => {
        const list = [...panel.querySelectorAll(LOOK_BUILDER_TABBABLE)].filter(
          (el) => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden' && !el.closest('[inert]')
        );
        if (panel.tabIndex >= 0) list.unshift(panel);
        return list;
      };
      const onKeydown = (event) => {
        if (!isOpen()) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          drawer.close();
          return;
        }
        if (event.key !== 'Tab') return;
        backwards = event.shiftKey;
        const list = stops();
        if (!list.length) return;
        const first = list[0];
        const last = list[list.length - 1];
        const active = document.activeElement;
        if (!panel.contains(active)) {
          event.preventDefault();
          (backwards ? last : first).focus();
        } else if (!backwards && active === last) {
          event.preventDefault();
          first.focus();
        } else if (backwards && active === first) {
          event.preventDefault();
          last.focus();
        }
      };
      // Safety net for stops the key check cannot see (e.g. inside a wallet
      // button's shadow root): focus that lands outside goes back in.
      const onFocusin = (event) => {
        if (!isOpen() || panel.contains(event.target)) return;
        const list = stops();
        const target = backwards ? list[list.length - 1] : list[0];
        if (target) target.focus();
      };
      document.addEventListener('keydown', onKeydown, true);
      document.addEventListener('focusin', onFocusin, true);
      this.releaseDrawer = () => {
        document.removeEventListener('keydown', onKeydown, true);
        document.removeEventListener('focusin', onFocusin, true);
        saved.forEach(([name, value]) => {
          if (value === null) panel.removeAttribute(name);
          else panel.setAttribute(name, value);
        });
        this.releaseDrawer = LookBuilder.noop;
      };
    }

    releaseDrawer() {}

    static noop() {}

    handleDrawerClosed(event) {
      const trigger = this.lastTrigger;
      if (!trigger || !event.target || event.target.tagName !== 'QUICK-CART-DRAWER') return;
      this.lastTrigger = null;
      this.releaseDrawer();
      trigger.setAttribute('aria-expanded', 'false');
      if (!trigger.isConnected) return;

      const cartDrawer = document.querySelector('cart-drawer');
      if (
        cartDrawer &&
        cartDrawer.classList.contains('is-visible') &&
        typeof cartDrawer.setActiveElement === 'function'
      ) {
        cartDrawer.setActiveElement(trigger);
        return;
      }

      const quickDrawer = event.target;
      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (!active || active === document.body || quickDrawer.contains(active)) {
          trigger.focus({ preventScroll: true });
        }
      });
    }
  }

  customElements.define('look-builder', LookBuilder);
}
